import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { LoyaltyModel } from "../models";
import { useAuth, useStore } from "../hooks";
import { GetUserLoyaltyData } from "../services";

type LoyaltyContextType = {
  isLoading: boolean;
  loyalty: LoyaltyModel | undefined;
  setLoyalty: (loyalty: LoyaltyModel | undefined) => void;
  updateLoyalty: () => void;
  loyaltyProducts: string[];
  loyaltyPoints: string | number;
  loyaltyAmount: string | number;
  currentLevel: string | number;
};

const LoyaltyContext = createContext<LoyaltyContextType | undefined>(undefined);

export const useLoyalty = () => {
  const context = useContext(LoyaltyContext);
  if (!context) {
    throw new Error("useLoyalty must be used within a LoyaltyProvider");
  }

  return context;
};

type LoyaltyProviderProps = {
  children?: ReactNode;
};

export function LoyaltyProvider({ children }: LoyaltyProviderProps) {
  const { storeId } = useStore();
  const { customer, isLoggedIn, isGuest } = useAuth();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loyalty, setLoyalty] = useState<LoyaltyModel | undefined>();

  const currentLevel = loyalty?.current_level ?? 0;
  const loyaltyPoints = loyalty?.loyalty?.points ?? 0;
  const loyaltyAmount = loyalty?.loyalty?.amount ?? 0;

  const loyaltyProducts =
    loyalty?.loyalty_data
      ?.filter(
        (item) =>
          item.level_benefit_type === "product" &&
          loyaltyPoints >= item.points_level,
      )
      ?.flatMap((item) =>
        item.level_benefit.split(",").map((productId) => productId.trim()),
      ) ?? [];

  // Using useCallback to avoid re-creating function on every render
  const updateLoyalty = useCallback(async () => {
    if (!isLoggedIn || isGuest) return;

    setIsLoading(true);
    try {
      const { status, data } = await GetUserLoyaltyData({
        store_id: storeId,
        customer_id: customer?.id,
      });

      if (status) {
        setLoyalty(data);
      }
    } catch (error) {
      console.error("Error fetching loyalty data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [storeId, customer?.id, isLoggedIn, isGuest]);

  useEffect(() => {
    updateLoyalty();
  }, [updateLoyalty]);

  return (
    <LoyaltyContext.Provider
      value={{
        isLoading,
        loyalty,
        setLoyalty,
        updateLoyalty,
        loyaltyProducts,
        loyaltyPoints,
        loyaltyAmount,
        currentLevel,
      }}
    >
      {children}
    </LoyaltyContext.Provider>
  );
}
